"use client";

import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  fetchApplications,
  setFilters,
  setPage,
  clearApplications,
} from "../../../redux/features/applicationLogSlice";
import ApplicationLogTable from "./ApplicationLogTable";
import { useCountries, useVisaTypeFilters } from "../../client/dashboard/staticData";
import { FiChevronDown, FiSearch } from "react-icons/fi";
import moment from "moment";
import DateSelector from "../../admin/HomePage/DateSelector";
import { setGlobalCountries } from "../../../redux/features/countriesSlice";
import LimitDropdown from '../../common/LimitDropdown';

export default function MainApplicationLog() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isInitialSyncDone, setIsInitialSyncDone] = useState(false);
  const countries = useCountries();
  const visaTypeFilters = useVisaTypeFilters();
  const { data, isLoading, error, filters, totalPages, hasInitiallyLoaded } =
    useSelector((state) => state.applications);

  // State for selected filters (local UI)
  const [selectedCategory, setSelectedCategory] = useState(filters.visaCategory || "All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const globalCountries = useSelector((state) => state.globalCountries.globalCountries);
  const [selectedCountries, setSelectedCountries] = useState(globalCountries || []);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);

  // Search state
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Date filter state
  const [fromMonth, setFromMonth] = useState(null);
  const [fromYear, setFromYear] = useState(null);
  const [toMonth, setToMonth] = useState(null);
  const [toYear, setToYear] = useState(null);

  const statusFilters = [
    "All",
    "Active/In Progress",
    "Pending",
    "Payment Pending",
    "Transaction Approval Pending",
    "Visa Applied",
    "FCC (Re-open)",
    "ART (Re-open)",
    "Withdrawn",
    "Cancelled",
    "Finalized",
  ];

  // Date change handler
  const handleDateChange = (type, value) => {
    switch (type) {
      case "fromMonth":
        setFromMonth(value);
        break;
      case "fromYear":
        setFromYear(value);
        break;
      case "toMonth":
        setToMonth(value);
        break;
      case "toYear":
        setToYear(value);
        break;
      default:
        break;
    }
  };

  // Handle country checkbox change
  const handleCheckboxChange = (countryName) => {
    setSelectedCountries((prevSelected) => {
      const updatedSelection = prevSelected.includes(countryName)
        ? prevSelected.filter((name) => name !== countryName)
        : [...prevSelected, countryName]
      dispatch(setGlobalCountries(updatedSelection))
      return updatedSelection
    })
  };

  const CustomDateSelector = () => (
    <DateSelector
      // className="flex w-full flex-col gap-3 sm:flex-row md:w-auto md:[&>div]:max-w-[150px] md:[&>div]:flex-none"
      fromMonth={fromMonth}
      setFromMonth={(value) => handleDateChange("fromMonth", value)}
      fromYear={fromYear}
      setFromYear={(value) => handleDateChange("fromYear", value)}
      toMonth={toMonth}
      setToMonth={(value) => handleDateChange("toMonth", value)}
      toYear={toYear}
      setToYear={(value) => handleDateChange("toYear", value)}
    />
  );

  // Compose dates for API only if dates are selected
  const fromDateStr = useMemo(() => {
    if (!fromYear || !fromMonth) return "";
    return `${fromYear}-${String(fromMonth).padStart(2, "0")}-01`;
  }, [fromYear, fromMonth]);
  const toDateStr = useMemo(() => {
    if (!toYear || !toMonth) return "";
    const endDay = moment(`${toYear}-${toMonth}`, "YYYY-M").daysInMonth();
    return `${toYear}-${String(toMonth).padStart(2, "0")}-${String(endDay).padStart(2, "0")}`;
  }, [toYear, toMonth]);

  // Handle global countries change (like in Leads)
  useEffect(() => {
    setSelectedCountries(globalCountries);
  }, [globalCountries]);

  // Handle initial mount with existing global countries
  useEffect(() => {
    if (globalCountries && globalCountries.length > 0) {
      dispatch(fetchApplications());
    } else if (!hasInitiallyLoaded) {
      dispatch(fetchApplications());
    }
  }, []); // Only run on mount

  // Sync page from URL on mount
  useEffect(() => {
    const pageParam = searchParams.get("page");
    if (pageParam && !isNaN(pageParam)) {
      const page = parseInt(pageParam, 10);
      if (page !== filters.page) {
        dispatch(setPage(page));
      }
    }
    setIsInitialSyncDone(true);
  }, []); // Run once on mount

  // Sync page from Redux to URL
  useEffect(() => {
    if (!isInitialSyncDone) return;

    const currentUrlPage = parseInt(searchParams.get("page") || "1", 10);
    const reduxPage = filters.page || 1;

    if (currentUrlPage !== reduxPage) {
      setSearchParams(
        (prev) => {
          prev.set("page", reduxPage.toString());
          return prev;
        },
        { replace: false },
      );
    }
  }, [filters.page, setSearchParams, searchParams, isInitialSyncDone]);

  // Update filters when local state changes (like in Leads)
  useEffect(() => {
    const visa = selectedCategory === "All" ? "" : selectedCategory;
    const apiStatus = mapStatusToApi(selectedStatus);
    const countries = selectedCountries.length > 0 ? selectedCountries.join(",") : "";

    dispatch(setFilters({
      visaCategory: visa,
      fromDate: fromDateStr,
      toDate: toDateStr,
      countries: countries,
      status: apiStatus,
      searchString: searchTerm,
    }));
  }, [dispatch, selectedCategory, selectedStatus, selectedCountries, fromDateStr, toDateStr, searchTerm]);

  // Fetch when filters change (like in Leads)
  useEffect(() => {
    dispatch(fetchApplications());
  }, [dispatch, filters.visaCategory, filters.fromDate, filters.toDate, filters.countries, filters.status, filters.searchString, filters.page, filters.limit]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Map UI status to API status - send exact values to API like leads
  const mapStatusToApi = (ui) => {
    switch (ui) {
      case "All":
        return "All";
      case "Active/In Progress":
        return "Active/In Progress";
      case "Pending":
        return "Pending";
      case "Payment Pending":
        return "Payment Pending";
      case "Transaction Approval Pending":
        return "Transaction Approval Pending";
      case "Visa Applied":
        return "VisaApplied";
      case "FCC (Re-open)":
        return "FCC (Re-open)";
      case "ART (Re-open)":
        return "ART (Re-open)";
      case "Withdrawn":
        return "Withdrawn";
      case "Cancelled":
        return "Cancelled" || "WithdrawnByClient";
      case "Finalized":
        return "Finalized";
      default:
        return ui; // Return as-is for any other status
    }
  };

  // Duplicate useEffects removed from here

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearApplications());
    };
  }, []);

  // Function to handle page changes
  const handlePageChange = (newPage) => {
    if (newPage !== filters.page) {
      dispatch(setPage(newPage));
      // The fetch will be triggered by the filters change effect
    }
  };

  return (
    <>
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-base font-normal md:text-xl lg:text-2xl lg:font-medium">Applications Log</h1>

        <div className="flex items-center w-full sm:w-[450px] justify-end mb-4">
          <div className="relative w-full max-w-xl">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or ID"
              className="w-full rounded-full border border-gray-200 py-2.5 pl-10 pr-4 focus:border-blue-500 focus:outline-none"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>
      </div>


      {/* Country Dropdown - Mobile Only */}
      <div className="mb-4 flex md:hidden justify-start">
        <div className="relative">
          <button
            onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
            className="flex items-center space-x-2 rounded-full p-1.5 md:p-2 text-sm border border-[#87CA60]/60 bg-white drop-shadow-md text-black min-w-[200px] justify-between"
          >
            <div className="flex items-center space-x-2">
              {selectedCountries.length > 0 ? (
                <>
                  <div className="flex items-center space-x-1">
                    {selectedCountries.length <= 8 &&
                      selectedCountries.map((name) => {
                        const country = countries.find((c) => c.name === name)
                        return country ? (
                          <img
                            key={country.name}
                            src={country.flag || "/placeholder.svg"}
                            alt={country.name}
                            className="w-6 h-6 rounded-full object-cover border border-slate-300"
                          />
                        ) : null
                      })}
                  </div>
                  {selectedCountries.length > 8 && (
                    <span className="text-sm text-gray-600">+{selectedCountries.length - 8} more</span>
                  )}
                </>
              ) : (
                <span className="text-gray-500">Select countries</span>
              )}
            </div>

            <svg
              className={`w-4 h-4 transition-transform ${isCountryDropdownOpen ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isCountryDropdownOpen && (
            <div className="absolute left-0 mt-2 w-[250px] bg-white border rounded-lg shadow-lg py-1 z-10 max-h-60 overflow-y-auto">
              {countries.map((country) => (
                <label
                  key={country.name}
                  className="w-full flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 space-x-2"
                >
                  <input
                    type="checkbox"
                    checked={selectedCountries.includes(country.name)}
                    onChange={() => handleCheckboxChange(country.name)}
                    className="cursor-pointer"
                  />
                  <img
                    src={country.flag || "/placeholder.svg"}
                    alt={country.name}
                    loading="lazy"
                    className="w-5 h-5 rounded-full object-cover"
                  />
                  <span className="text-black">{country.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dropdown for Small Screens - Visa Type */}
      <div className="md:hidden mt-4 mb-4">
        <label className="text-sm text-gray-700 mb-1 block">Select Visa Type</label>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="w-full p-2 border rounded-md text-gray-700"
        >
          {visaTypeFilters.map((category) => (
                <option key={category?._id} value={category?.title}>
                  {category?.title}
                </option>
          ))}
        </select>
      </div>

      {/* Application Status Dropdown for Small Screens */}
      <div className="md:hidden mt-4 mb-4">
        <label className="text-sm text-gray-700 mb-1 block">Application Status</label>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="w-full p-2 border rounded-md text-gray-700 text-sm md:text-base"
        >
          {statusFilters.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
      </div>

      {/* Date Filters for Small Screens */}
      <div className="md:hidden mt-4 mb-4">
        <CustomDateSelector />
      </div>

      {/* Filters row: Desktop Only */}
      <div className="hidden md:block w-full mb-6">
        <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 md:flex md:flex-wrap md:flex-1 md:items-end md:gap-2">
            <div className="relative w-full md:w-[180px] md:flex-none">
              <select
                className="w-full max-w-full appearance-none truncate rounded border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#005787]"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                title={selectedCategory}
              >
                {visaTypeFilters.map((category) => (
                  <option key={category?._id} value={category?.title}>
                    {category?.title}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500" />
            </div>

            <div className="relative w-full md:w-[180px] md:flex-none">
              <select
                className="w-full max-w-full appearance-none truncate rounded border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#005787]"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                title={selectedStatus}
              >
                {statusFilters.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500" />
            </div>
          </div>

          <div className="flex justify-end md:justify-start md:flex-none md:min-w-[260px]">
            <div className="w-full md:w-auto">
              <CustomDateSelector />
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Render the table - most filtering done on API side */}
          <ApplicationLogTable
            applications={(() => {
              const rowsRaw = Array.isArray(data.applications) ? data.applications : [];

              // Note: Search and most status filtering is now done on the API side
              // Only keep client-side filtering for complex statuses that require special logic
              
              const rowsFiltered = rowsRaw.filter((row) => {
                if (selectedStatus === "All") return true;
                
                // These complex statuses still need client-side logic
                switch (selectedStatus) {
                  case "Active/In Progress":
                    return !!(row.agent && row.agent.firstName);
                  case "Pending":
                    return !row.agent || row.agent === "" || !row.agent.firstName;
                  case "Payment Pending":
                    return row.paymentStatus === "Not Paid" || row.paymentStatus === "Pending";
                  case "Transaction Approval Pending":
                    return row.paymentStatus === "Pending";
                  default:
                    // For simple statuses (Withdrawn, Cancelled, Finalized, VisaApplied, etc.)
                    // API handles the filtering, so return all rows
                    return true;
                }
              });

              // Sort by date (latest first) - handle locally
              const rows = [...rowsFiltered].sort((a, b) => {
                const dateA = new Date(a.createdAt || a.updatedAt || 0);
                const dateB = new Date(b.createdAt || b.updatedAt || 0);
                return dateB - dateA; // Descending order (latest first)
              });

              return rows;
            })()}
          />
          {/* if there's no data, show a message */}
          {data.applications.length === 0 && (
            <div className="flex items-center justify-center min-h-[400px]">
              <p className="text-gray-500">No applications found.</p>
            </div>
          )}

          {/* Pagination Controls */}
            <div className="flex items-center justify-center gap-2 mt-4">
          {totalPages > 1 && data.applications.length > 0 && (
<>

              <button
                onClick={() => handlePageChange(Math.max(filters.page - 1, 1))}
                disabled={filters.page === 1}
                className={`px-2 md:px-3 py-2 rounded-md ${filters.page === 1
                    ? "bg-gray-200/50 text-gray-400 cursor-default"
                    : "bg-gray-100 text-[#005787] hover:bg-gray-300"
                  }`}
              >
                &lt;
              </button>

              {/* Page Numbers */}
              {Array.from({ length: totalPages }).map((_, index) => {
                const pageNumber = index + 1;
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= filters.page - 1 &&
                    pageNumber <= filters.page + 1)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`px-3 md:px-4 py-1.5 md:py-2 rounded-md border ${filters.page === pageNumber
                          ? "bg-[#005787] text-white border-[#334155]"
                          : "bg-gray-100 text-[#005787] hover:bg-gray-300 border-gray-200"
                        }`}
                    >
                      {pageNumber}
                    </button>
                  );
                }
                if (
                  pageNumber === filters.page - 2 ||
                  pageNumber === filters.page + 2
                ) {
                  return (
                    <span key={pageNumber} className="px-2 py-1 text-gray-500">
                      ...
                    </span>
                  );
                }
                return null;
              })}

              {/* Next Button */}
              <button
                onClick={() =>
                  handlePageChange(Math.min(filters.page + 1, totalPages))
                }
                disabled={filters.page === totalPages}
                className={`px-2 md:px-3 py-2 rounded-md ${filters.page === totalPages
                    ? "bg-gray-200/50 text-gray-400 cursor-default"
                    : "bg-gray-100 text-[#005787] hover:bg-gray-300"
                  }`}
              >
                &gt;
              </button>
              </>
              )}
              <div className="hidden md:flex items-center gap-3">
            <LimitDropdown value={filters.limit || 10} onChange={(v) => { dispatch(setFilters({ ...filters, limit: v })); dispatch(setPage(1)); }} />
                          </div>
            </div>
        </>
      )}
    </>
  );
}
